import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { timeout } from 'ember-concurrency';
import type { Hotel, Hotelinfo } from 'frontend-utils/models';
import type { BenchmarkInsightCompetitorsCompetitor } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  COMPETITORS_MAP_ICONS,
  COMPETITORS_MAP_OPTIONS,
  COMPETITORS_MAP_STYLES,
} from 'lumen/utils/components/hotel/benchmark-insight/competitors/map/competitors-map';
import { getGoogleMapsStyles } from 'lumen/utils/constants/google-maps-constants';

interface HotelBenchmarkInsightCompetitorsMapCompetitorsMapSignature {
  Args: {
    compset: BenchmarkInsightCompetitorsCompetitor[];
    showSuggestedCompetitors?: boolean;
  };
  Element: HTMLDivElement;
}

export default class HotelBenchmarkInsightCompetitorsMapCompetitorsMap extends Component<HotelBenchmarkInsightCompetitorsMapCompetitorsMapSignature> {
  @service('benchmark-insight/beni-competitors-service')
  declare benchmarkInsightBeniCompetitorsService: Services['benchmark-insight/beni-competitors-service'];

  @service('benchmark-insight/competitors/desired-compset')
  declare benchmarkInsightCompetitorsDesiredCompset: Services['benchmark-insight/competitors/desired-compset'];

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @tracked map: google.maps.Map | null = null;
  @tracked markers: Record<
    // @ts-expect-error: Type 'string | number | null' does not satisfy the constraint 'string | number | symbol'.
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    Competitor['id'] | Hotelinfo['id'],
    google.maps.Marker
  > = {};

  get hotel(): Hotel {
    return this.hotelAndMarketService.hotel;
  }

  get hoveredItem(): BenchmarkInsightCompetitorsCompetitor | null {
    return this.benchmarkInsightBeniCompetitorsService.hoveredCompetitor;
  }

  get lastHoveredItem(): BenchmarkInsightCompetitorsCompetitor | null {
    return this.benchmarkInsightBeniCompetitorsService.lastHoveredCompetitor;
  }

  centeredOnOwnHotelPosition(): google.maps.LatLngLiteral {
    const ownHotelInfo = this.hotel?.hotelinfo;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const ownMarker = this.getMarkerByHotelinfo(ownHotelInfo);

    if (!ownMarker) {
      return {
        lat: ownHotelInfo.latitude,
        lng: ownHotelInfo.longitude,
      };
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const ownMarkerPosition = ownMarker.getPosition();

    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      lat: ownMarkerPosition?.lat() ?? 0,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      lng: ownMarkerPosition?.lng() ?? 0,
    };
  }

  fitMapToMarkers(): void {
    const bounds = new google.maps.LatLngBounds();
    const markers = Object.values(this.markers);
    markers.forEach((marker) => {
      const position = marker.getPosition();

      if (position) {
        bounds.extend(position);
      }
    });
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.initialisePanEffect(bounds);
  }

  getCenter(ownHotel: Hotel): google.maps.LatLng {
    return new google.maps.LatLng(
      ownHotel.hotelinfo.latitude,
      ownHotel.hotelinfo.longitude,
    );
  }

  getIconForCompetitor(
    competitor: BenchmarkInsightCompetitorsCompetitor,
  ): google.maps.Icon | google.maps.Symbol {
    const isCurrentlyHovered = this.hoveredItem?.id === competitor.id;
    const isSuggestedCompetitor =
      this.benchmarkInsightCompetitorsDesiredCompset.isSuggestedCompetitor(
        competitor,
      );

    if (this.args.showSuggestedCompetitors && isSuggestedCompetitor) {
      return isCurrentlyHovered
        ? COMPETITORS_MAP_ICONS.COMPSET_SUGGESTED_HIGHLIGHTED
        : COMPETITORS_MAP_ICONS.COMPSET_SUGGESTED;
    }

    return isCurrentlyHovered
      ? COMPETITORS_MAP_ICONS.HIGHLIGHTED
      : COMPETITORS_MAP_ICONS.COMPSET;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getMarkerByHotelinfo(
    competitor: Hotelinfo | BenchmarkInsightCompetitorsCompetitor,
  ) {
    // @ts-expect-error: Type 'null' cannot be used as an index type.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const marker = this.markers[competitor.id];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return marker ?? null;
  }

  async initialisePanEffect(bounds: google.maps.LatLngBounds): Promise<void> {
    await timeout(500);
    this.map?.fitBounds(bounds);
  }

  markOtherHotels(): void {
    this.args.compset.forEach((competitor) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const existingMarker = this.getMarkerByHotelinfo(competitor);
      if (existingMarker) {
        return;
      }
      const icon = this.getIconForCompetitor(competitor);
      const newMarker = this.setMarker(competitor, icon, 10);
      this.setMarkerListeners(newMarker, competitor);
    });
  }

  markOwnHotel(): void {
    this.setMarker(this.hotel.hotelinfo, COMPETITORS_MAP_ICONS.OWN, 20);
  }

  purgeDeletedCompetitorMarkers(): void {
    const markersArray = Object.entries(this.markers);
    markersArray
      .filter(([id]) => id !== this.hotel.hotelinfo.id)
      .forEach(([id, marker]) => {
        const isCompetitorInCompset = this.args.compset.some(
          (competitor) => competitor.id === id,
        );
        if (!isCompetitorInCompset) {
          marker.setMap(null);
          delete this.markers[id];
        }
      });
  }

  purgeMarkers(): void {
    Object.values(this.markers).forEach((marker) => {
      marker.setMap(null);
    });
    this.markers = {};
  }

  setBoundsForMap(): void {
    if (Object.keys(this.markers).length > 1) {
      this.fitMapToMarkers();
    } else {
      this.map?.setCenter(this.centeredOnOwnHotelPosition());
      this.map?.setZoom(14);
    }
  }

  setMarker(
    competitor: Hotelinfo | BenchmarkInsightCompetitorsCompetitor,
    icon: google.maps.Icon | google.maps.Symbol,
    zIndex: number,
  ): google.maps.Marker {
    const { latitude, longitude, name } = competitor;

    // This is where all markers are to be created, nowhere else!
    const newMarker = new google.maps.Marker({
      position: {
        lat: latitude,
        lng: longitude,
      },
      map: this.map,
      title: name,
      optimized: false,
      zIndex,
      icon,
    });

    // @ts-expect-error: A computed property name must be of type 'string', 'number', 'symbol', or 'any'.
    this.markers = { ...this.markers, [competitor.id]: newMarker };
    return newMarker;
  }

  setMarkerListeners(
    marker: google.maps.Marker,
    competitor: BenchmarkInsightCompetitorsCompetitor,
  ): void {
    marker.addListener('mouseover', () => {
      this.benchmarkInsightBeniCompetitorsService.handleHover(competitor);
    });
    marker.addListener('mouseout', () => {
      this.benchmarkInsightBeniCompetitorsService.handleHover();
    });
    marker.addListener('click', () => {
      this.benchmarkInsightBeniCompetitorsService.redirectToSource(competitor);
    });
  }

  @action
  addMap(mapElement: HTMLElement): void {
    const ownHotel = this.hotel;
    const center = this.getCenter(ownHotel);

    this.map = new google.maps.Map(mapElement, {
      center,
      ...COMPETITORS_MAP_OPTIONS,
      styles: [
        ...COMPETITORS_MAP_STYLES,
        ...getGoogleMapsStyles().BLACK_AND_WHITE,
      ],
    });
  }

  @action
  didInsertMapElement(mapElement: HTMLElement): void {
    this.purgeMarkers();
    this.addMap(mapElement);
    this.markOwnHotel();
    this.markOtherHotels();
    this.setBoundsForMap();
  }

  @action
  didUpdateCompset(): void {
    this.purgeDeletedCompetitorMarkers();
    this.markOtherHotels();
    this.setBoundsForMap();
  }

  @action
  handleHover(): void {
    const hoveredItem = this.hoveredItem ?? this.lastHoveredItem;

    if (!hoveredItem) {
      return;
    }

    this.highlightCompetitor(hoveredItem);
  }

  @action
  highlightCompetitor(competitor: BenchmarkInsightCompetitorsCompetitor): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const marker = this.getMarkerByHotelinfo(competitor);
    const icon = this.getIconForCompetitor(competitor);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    marker?.setIcon(icon);
  }

  <template>
    <div
      class="beni-compset-panel__container__content__map-container"
      data-test-id="competitors-map"
    >
      <div
        class="h-full"
        id="competitor-map"
        {{didInsert this.didInsertMapElement}}
        {{didUpdate this.handleHover this.hoveredItem}}
        {{didUpdate this.didUpdateCompset @compset}}
        ...attributes
      ></div>
    </div>
  </template>
}
